import { FC, memo, useCallback } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import { RangeFilter } from "@/components/filters/Ranges/RangeFilter";
import type { CompanyFilters, FilterChip } from "../types";
import { useCompanyFiltersStore } from "../provider/CompanyFilterProvider";
import { buildRangeLabel } from "./buildRangeLabel";

/**
 * Generic slice type for range-based filters (employees, revenue, etc.)
 */
export type RangeSlice = {
  min?: number;
  max?: number;
  hasFixed: boolean;
};

/** Filter module structure (defined inline to avoid circular deps) */
type RangeFilterModule<K extends RangeModuleConfig["key"]> = {
  key: K;
  defaultSlice: RangeSlice;
  Component: FC;
  toCompanyFilters: (slice: RangeSlice) => Partial<CompanyFilters>;
  fromCompanyFilters: (filters: CompanyFilters) => RangeSlice;
  getChips: (
    slice: RangeSlice,
    setSlice: (slice: RangeSlice) => void
  ) => FilterChip[];
  getCount: (slice: RangeSlice) => number;
};

export const defaultRangeSlice: RangeSlice = {
  min: undefined,
  max: undefined,
  hasFixed: false,
};

type RangeModuleConfig = {
  /** Unique key for this filter module */
  key: "employees" | "revenue";
  /** API field name for the range (e.g., "employees", "revenue") */
  apiField: keyof CompanyFilters;
  /** API field name for the "hasFixed" boolean (e.g., "hasFixedEmployees") */
  apiFixedField: keyof CompanyFilters;
  /** Label for the checkbox */
  checkboxLabel: string;
  /** Optional prefix for the range input (e.g., "$" for revenue) */
  prefix?: string;
  /** Display label for chips (e.g., "Employees", "Revenue") */
  chipLabel: string;
  /** Whether this is a currency range (affects formatting) */
  isCurrency?: boolean;
};

function createRangeFilterComponent(config: RangeModuleConfig): FC {
  const RangeFilterComponent: FC = memo(() => {
    const slice = useCompanyFiltersStore(
      (s) => s.slices[config.key]
    ) as RangeSlice;
    const setSlice = useCompanyFiltersStore((s) => s.setSlice);

    const rangeHasValues = slice.min !== undefined || slice.max !== undefined;

    const handleRangeChange = useCallback(
      (min: number | undefined, max: number | undefined) => {
        if (min === undefined && max === undefined) {
          setSlice(config.key, defaultRangeSlice);
          return;
        }
        setSlice(config.key, { ...slice, min, max });
      },
      [setSlice, slice]
    );

    const handleFixedChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setSlice(config.key, {
          ...slice,
          hasFixed: event.target.checked,
        });
      },
      [setSlice, slice]
    );

    return (
      <Box
        id={`${config.key}-filter-container`}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <RangeFilter
          value={{ min: slice.min, max: slice.max }}
          onChange={handleRangeChange}
          prefix={config.prefix}
          updatedOnType
        />
        <FormControlLabel
          id={`${config.key}-filter-fixed-checkbox`}
          control={
            <Checkbox
              size="small"
              checked={slice.hasFixed}
              onChange={handleFixedChange}
              disabled={!rangeHasValues}
            />
          }
          label={
            <Typography variant="body2">{config.checkboxLabel}</Typography>
          }
          sx={{ ml: 0 }}
        />
      </Box>
    );
  });

  RangeFilterComponent.displayName = `${config.key}Filter`;
  return RangeFilterComponent;
}

/**
 * Creates a filter module for range-based filters (employees, revenue, etc.)
 * Reduces boilerplate by encapsulating common patterns.
 */
export function createRangeModule<const K extends RangeModuleConfig["key"]>(
  config: RangeModuleConfig & { key: K }
): RangeFilterModule<K> {
  const Component = createRangeFilterComponent(config);

  return {
    key: config.key,
    defaultSlice: defaultRangeSlice,
    Component,
    toCompanyFilters: (slice: RangeSlice): Partial<CompanyFilters> => {
      if (slice.min === undefined && slice.max === undefined) return {};
      const out: Partial<CompanyFilters> = {
        [config.apiField]: { min: slice.min, max: slice.max },
      };
      if (slice.hasFixed) {
        (out as Record<string, unknown>)[config.apiFixedField] = true;
      }
      return out;
    },
    fromCompanyFilters: (filters: CompanyFilters): RangeSlice => {
      const range = filters[config.apiField] as
        | { min?: number; max?: number }
        | undefined;
      const min = range?.min ?? undefined;
      const max = range?.max ?? undefined;
      const hasRange = min !== undefined || max !== undefined;
      return {
        min,
        max,
        hasFixed: hasRange ? !!filters[config.apiFixedField] : false,
      };
    },
    getChips: (slice, setSlice) => {
      const hasValues = slice.min !== undefined || slice.max !== undefined;
      if (!hasValues) return [];

      const rangeLabel = buildRangeLabel(slice.min, slice.max, {
        isCurrency: config.isCurrency,
      });
      const fixedSuffix = slice.hasFixed ? " (fixed)" : "";

      return [
        {
          id: config.key,
          label: `${config.chipLabel}: ${rangeLabel}${fixedSuffix}`,
          filterKey: config.key,
          onRemove: () =>
            setSlice({ min: undefined, max: undefined, hasFixed: false }),
        },
      ];
    },
    getCount: (slice) => {
      return slice.min !== undefined || slice.max !== undefined ? 1 : 0;
    },
  };
}
